import React, { useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Calendar, MapPin, Clock, Volume2, VolumeX, Sparkles } from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import {
  extractCoupleNames,
  parseFlexibleDate,
} from '@/src/infrastructure/lib/weddingDataHelper';
import { safeImageUrl, safeLinkUrl } from '@/src/modules/renderer/url-safety';
import { UnboxingGate } from '../_shared/UnboxingGate';
import './theme.css';

const META: TemplateMetadata = {
  id: 'exp-emerald-noir-reception',
  name: 'Emerald Noir Reception',
  occasionType: 'WEDDING',
  description:
    'After-dark cocktail & reception invite: obsidian, emerald and gold foil, wax-seal unboxing gate, glass cards and RSVP.',
  isPremium: true,
  tags: ['wedding', 'reception', 'cocktail', 'dark', 'emerald', 'luxury', 'premium', 'evening'],
  colorScheme: { primary: '#0B3D2E', accent: '#D4AF37', bg: '#050807' },
};

function pickStr(...vals: unknown[]): string {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

export function EmeraldNoirReception({ data }: { data: InvitationData }) {
  const [muted, setMuted] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const expMeta = (data.customData as { expMeta?: Record<string, unknown> } | null)?.expMeta ?? {};
  const wedding = data.customData?.wedding;
  const extracted = extractCoupleNames(data.eventName);

  const groomName = pickStr(
    wedding?.groom?.fullName,
    wedding?.groom?.nickname,
    expMeta.groomName,
    extracted?.groom,
  );
  const brideName = pickStr(
    wedding?.bride?.fullName,
    wedding?.bride?.nickname,
    expMeta.brideName,
    extracted?.bride,
  );
  const coupleTitle =
    groomName && brideName ? `${groomName} & ${brideName}` : groomName || brideName || data.eventName || '';

  const parsedDate = parseFlexibleDate(data.eventDate);
  const eventDateLabel = parsedDate
    ? parsedDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : pickStr(data.eventDate);

  const venue = pickStr(data.eventVenue, wedding?.weddingEvents?.[0]?.venue);
  const hostName = pickStr(data.hostName);
  const tagline = pickStr(
    data.customMessage,
    expMeta.tagline as string,
    'An evening of champagne, music, and celebration under the stars.',
  );

  const bridePhoto = safeImageUrl(
    pickStr(
      (expMeta.referenceImages as { bride?: string } | undefined)?.bride,
      expMeta.bridePhotoUrl as string,
    ),
  );
  const groomPhoto = safeImageUrl(
    pickStr(
      (expMeta.referenceImages as { groom?: string } | undefined)?.groom,
      expMeta.groomPhotoUrl as string,
    ),
  );
  const couplePhoto = safeImageUrl(
    pickStr(
      (expMeta.referenceImages as { couple?: string; primary?: string } | undefined)?.couple,
      (expMeta.referenceImages as { primary?: string } | undefined)?.primary,
      data.mediaUrls?.[0],
    ),
  );
  const customVideoUrl = safeLinkUrl(
    pickStr(
      (data.customData as { customVideoUrl?: string } | null)?.customVideoUrl,
      expMeta.customVideoUrl as string,
    ),
  );
  const rsvpHref = safeLinkUrl(data.rsvpLink);

  const mapsUrl = venue
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`
    : '';

  const toggleAudio = () => {
    const el = audioRef.current;
    if (!el) {
      setMuted((m) => !m);
      return;
    }
    if (muted) {
      void el.play().catch(() => undefined);
      setMuted(false);
    } else {
      el.pause();
      setMuted(true);
    }
  };

  const body = (
    <div className="enr-root" data-template="exp-emerald-noir-reception">
      <audio
        ref={audioRef}
        loop
        preload="none"
        src="https://cdn.pixabay.com/download/audio/2021/08/04/audio_0625c1539c.mp3?filename=cinematic-ambient-116199.mp3"
      />
      <button type="button" className="enr-audio" onClick={toggleAudio} aria-label={muted ? 'Play audio' : 'Mute audio'}>
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      <section className="enr-hero">
        <p className="enr-eyebrow">
          <Sparkles size={12} /> Evening Reception
        </p>
        <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="enr-names">
          {coupleTitle || 'Reception'}
        </motion.h1>
        <p className="enr-event">{data.eventName || 'Cocktail Reception'}</p>
        <div className="enr-glass enr-meta">
          {eventDateLabel ? (
            <span>
              <Calendar size={14} /> {eventDateLabel}
            </span>
          ) : null}
          {data.eventTime ? (
            <span>
              <Clock size={14} /> {data.eventTime}
            </span>
          ) : null}
          {venue ? (
            <span>
              <MapPin size={14} /> {venue}
            </span>
          ) : null}
        </div>
        {hostName ? <p className="enr-host">Hosted by {hostName}</p> : null}
        <a className="enr-cta" href="#enr-rsvp">
          Confirm attendance
        </a>
      </section>

      {(bridePhoto || groomPhoto || couplePhoto) && (
        <section className="enr-photos">
          {groomPhoto ? (
            <figure>
              <img src={groomPhoto} alt={groomName || 'Groom'} />
              <figcaption>{groomName || 'Groom'}</figcaption>
            </figure>
          ) : null}
          {couplePhoto ? (
            <figure className="enr-couple">
              <img src={couplePhoto} alt={coupleTitle || 'Couple'} />
              <figcaption>The couple</figcaption>
            </figure>
          ) : null}
          {bridePhoto ? (
            <figure>
              <img src={bridePhoto} alt={brideName || 'Bride'} />
              <figcaption>{brideName || 'Bride'}</figcaption>
            </figure>
          ) : null}
        </section>
      )}

      <section className="enr-glass enr-copy">
        <p>{tagline}</p>
      </section>

      {customVideoUrl ? (
        <section className="enr-video">
          <h2>Our Pre-Wedding Story & Special Memories</h2>
          <video controls playsInline src={customVideoUrl} />
        </section>
      ) : null}

      {mapsUrl ? (
        <section className="enr-map">
          <a href={mapsUrl} target="_blank" rel="noreferrer">
            Venue directions
          </a>
        </section>
      ) : null}

      <section id="enr-rsvp" className="enr-glass enr-rsvp">
        <h2>RSVP</h2>
        <p>We cannot wait to celebrate with you.</p>
        {rsvpHref ? (
          <a className="enr-cta" href={rsvpHref}>
            Open RSVP
          </a>
        ) : (
          <p className="enr-hint">RSVP unlocks after publish.</p>
        )}
      </section>
    </div>
  );

  return (
    <UnboxingGate title={coupleTitle || 'Reception'} accent="#D9B65A" background="#041510">
      {body}
    </UnboxingGate>
  );
}

registry.register({ metadata: META, component: EmeraldNoirReception });
